// Global Types
import type {
  $Translate,
} from 'common.language';
import type {
  $Model,
  $ModelUpsertParams,
} from 'common.data';

// Global Enums
import {
  ChampionshipAdminRequestStatus,
} from 'common.data';
import {
  adminPages,
  adminSections,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Col,
  Row,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  InputCheckbox,
  Loader,
  SectionHeader,
  TdDate,
  TdImage,
} from '@vroskus/library-components';

// Helpers
import _ from 'lodash';
import {
  cleanFormValues,
  getImageUrl,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  getPreviousRegionSeasonId,
  getRegionByRegionSeasonId,
  getRegionSeasonByRegionSeasonId,
  getShortItemUrl,
} from 'common.helpers/dist/browser';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
  publicBorder,
} from '../../../helpers';

// Components
import Page from '../../layout/Page';
import EmptyTable from '../../common/EmptyTable';
import TdUrl from '../../common/TdUrl';
import ChampionshipAdminRequestsSection from '../../sections/ChampionshipsPageSections/ChampionshipAdminRequestsSection';
import ChampionshipAdminProposesSection from '../../sections/ChampionshipsPageSections/ChampionshipAdminProposesSection';
import ChampionshipMinimalFields from '../../fields/ChampionshipMinimalFields';
import ThQty from '../../common/ThQty';
import InputRegionSeason from '../../form/InputRegionSeason';

// Helpers
import config from '../../../config';

// Services
import Services from '../../../services';

// Types
import type {
  $ReducerState,
} from '../../../redux';
import type {
  $RedirectAction,
} from '../../../redux/modules/main/actions';

export type $OwnProps = $Translate;

export type $StateProps = {
  auth: $ReducerState<'auth'>;
  main: $ReducerState<'main'>;
};

export type $DispatchProps = {
  redirect: $RedirectAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'Championship'>>;
  loading: boolean;
  referenceData: Array<$Model<'Championship'>>;
  resourceDataChampionship: Array<$Model<'Championship'>>;
};

const pageId = adminPages.championshipsPage;

class ChampionshipsPageView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
      referenceData: [],
      resourceDataChampionship: [],
    };
  }

  componentDidMount(): void {
    this.getDataWithLoading();
    this.getResourceData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      main,
    } = this.props;

    if (main.activeRegionSeasonId !== null && isDiff(
      main.activeRegionSeasonId,
      prevProps.main.activeRegionSeasonId,
    )) {
      this.getDataWithLoading();
      this.getResourceData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          main,
        } = this.props;

        const models = ['Championship', 'ChampionshipAdminRequest'];

        if (main.activeRegionSeasonId === data.RegionSeasonId) {
          if (data.modelName === 'Championship') {
            this.getData();
          }

          if (_.includes(
            models,
            data.modelName,
          )) {
            this.getResourceData();
          }
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getDataWithLoading(): Promise<void> {
    await this.setStateAsync({
      loading: true,
    });

    await this.getData();
  }

  async getData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegionSeasonId = main.activeRegionSeasonId;

    await defaultGetData({
      action: async () => {
        if (RegionSeasonId !== null) {
          const data = await Services.Api.getManyChampionship({
            RegionSeasonId,
          });
          const previousRegionSeasonId = getPreviousRegionSeasonId({
            regions: main.regions,
            RegionSeasonId,
          });
          const referenceData = previousRegionSeasonId ? await Services.Api
            .getResourceChampionship({
              RegionSeasonId: previousRegionSeasonId,
            }) : [];

          await this.setStateAsync({
            data,
            loading: false,
            referenceData,
          });
        }
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  async getResourceData(): Promise<void> {
    const {
      main,
    } = this.props;

    const RegionSeasonId = main.activeRegionSeasonId;

    await defaultGetData({
      action: async () => {
        if (RegionSeasonId !== null) {
          const resourceDataChampionship = await Services.Api.getResourceChampionship({
            RegionSeasonId,
          });

          await this.setStateAsync({
            resourceDataChampionship,
          });
        }
      },
    });
  }

  renderHeaderRequestButton() {
    const {
      auth,
      main,
      t,
    } = this.props;
    const {
      data,
      resourceDataChampionship,
    } = this.state;

    if (auth.user !== null) {
      const newItem: $ModelUpsertParams<'ChampionshipAdminRequest'> = {
        ChampionshipId: null,
        Email: auth.user.Email,
        Name: auth.user.Name,
        Status: ChampionshipAdminRequestStatus.propose,
      };

      const filteredResourceDataChampionship = resourceDataChampionship.filter((
        regatta: $Model<'Championship'>,
      ) => !_.find(
        data,
        {
          id: regatta.id,
        },
      ));

      const RegionSeasonId = main.activeRegionSeasonId;
      let regionName = '';
      let regionSeasonName = '';

      if (RegionSeasonId !== null) {
        regionName = getRegionByRegionSeasonId({
          regions: main.regions,
          RegionSeasonId,
        }).Name;

        regionSeasonName = getRegionSeasonByRegionSeasonId({
          regions: main.regions,
          RegionSeasonId,
        }).Name;
      }

      return (
        <FormDialog
          fields={(
            <>
              <div className={'d-flex flex-row w-100 align-items-center justify-content-between mb-1'}>
                <h4 className={'h4 mb-0'}>
                  {`${regionName}: ${regionSeasonName}`}
                </h4>
                <small className={'text-muted lh-1'}>
                  <span>
                    {`${t('Found items')}: `}
                  </span>
                  <span className={'text-bold'}>
                    {filteredResourceDataChampionship.length}
                  </span>
                </small>
              </div>
              {filteredResourceDataChampionship.length === 0 ? (
                <EmptyTable />
              ) : (
                <hr />
              )}
              {filteredResourceDataChampionship.map((resourceItem: $Model<'Championship'>) => (
                <InputCheckbox
                  description={resourceItem.OrganizerName}
                  key={resourceItem.id}
                  label={resourceItem.Name}
                  name={resourceItem.id}
                />
              ))}
            </>
          )}
          id={'request-championship-control-dialog'}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const actions: Array<Promise<unknown>> = [];

              Object.keys(values).forEach((ChampionshipId: string) => {
                if (values[ChampionshipId]) {
                  const params: $ModelUpsertParams<'ChampionshipAdminRequest'> = {
                    ChampionshipId,
                    ...newItem,
                  };

                  const action = Services.Api.upsertChampionshipAdminRequest(params);

                  actions.push(action);
                }
              });

              await Promise.all(actions);
            },
          })}
          overflowControl
          subTitle={t('Select championships you want to control')}
          title={t('Championships')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={'request-championship-control-button'}
            size={'sm'}
          >
            {t('I want to control championship')}
          </Button>
        </FormDialog>
      );
    }

    return null;
  }

  renderHeaderCreateButton() {
    const {
      auth,
      main,
      t,
    } = this.props;
    const {
      referenceData,
    } = this.state;

    if (main.activeRegionSeasonId !== null && auth.user !== null) {
      const RegionSeasonId = main.activeRegionSeasonId;

      const newItem: $ModelUpsertParams<'Championship'> = {
        Content: null,
        Image: null,
        Lock: false,
        Name: '',
        OrganizerEmail: auth.user.Email,
        OrganizerName: null,
        Public: true,
        ReferenceId: null,
        RegionSeasonId,
        Slug: null,
      };

      return (
        <FormDialog
          fields={(
            <ChampionshipMinimalFields
              item={newItem}
              referenceData={referenceData}
            />
          )}
          id={'create-championship-dialog'}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'Championship'>>({
                ...newItem,
                ...values,
              });

              await Services.Api.upsertChampionship(params);
            },
          })}
          subTitle={t('New')}
          title={t('Championship')}
        >
          <Button
            color={'info'}
            id={'create-championship-button'}
            size={'sm'}
            title={t('Add new championship')}
          >
            {t('I want to create championship')}
          </Button>
        </FormDialog>
      );
    }

    return null;
  }

  renderHeader() {
    const {
      t,
    } = this.props;

    return (
      <Card id={adminSections[pageId].header}>
        <SectionHeader
          iconClass={'icon-organization'}
          rightButtons={(
            <Row>
              <Col
                className={'mb-1 mb-lg-0'}
                lg={5}
              >
                <InputRegionSeason />
              </Col>
              <Col
                className={'d-flex flex-row justify-content-end text-nowrap'}
                lg={7}
              >
                {this.renderHeaderRequestButton()}
                {this.renderHeaderCreateButton()}
              </Col>
            </Row>
          )}
          title={t('Championships')}
        />
      </Card>
    );
  }

  renderChampionships() {
    const {
      main,
      redirect,
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return (
      <>
        {data.map((dataItem: $Model<'Championship'>) => {
          const {
            id,
          } = dataItem;

          return (
            <tr
              key={id}
              style={publicBorder(dataItem.Public)}
            >
              <TdImage
                largeImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: dataItem.Image,
                  size: 'max',
                })}
                noImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: null,
                  size: 'min',
                })}
                smallImageUrl={getImageUrl({
                  cdnUrl: config.CDN_URL,
                  image: dataItem.Image,
                  size: 'min',
                })}
                value={dataItem.Image}
              />
              <td>{dataItem.Name}</td>
              <TdUrl
                value={getShortItemUrl({
                  basePath: '/championship',
                  item: dataItem,
                  mainDomain: config.DOMAIN,
                  regions: main.regions,
                })}
              />
              <TdDate
                duration
                multiple
                value={dataItem.createdAt}
              />
              <td>
                <div className={'d-flex justify-content-end'}>
                  <Button
                    className={'mr-1'}
                    color={'warning'}
                    id={`redirect-championship-page-button-${id}`}
                    onClick={() => redirect({
                      pathname: `/championship/${id}`,
                    })}
                    outline
                    size={'sm'}
                  >
                    {t('Edit')}
                  </Button>
                  <DeleteDialog
                    content={t('Do you really want to delete?')}
                    id={`delete-championship-dialog-${id}`}
                    labelCloseButton={t('Close')}
                    labelDeleteButton={t('Delete')}
                    onAction={() => defaultDeleteItem({
                      action: async () => Services.Api.deleteChampionship(dataItem),
                    })}
                    subTitle={dataItem.Name}
                    title={t('Championship')}
                  >
                    <Button
                      color={'danger'}
                      id={`delete-championship-button-${id}`}
                      outline
                      size={'sm'}
                    >
                      {t('Delete')}
                    </Button>
                  </DeleteDialog>
                </div>
              </td>
            </tr>
          );
        })}
      </>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable className={'mt-5'} />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Logo')}</th>
            <th>{t('Name')}</th>
            <th>{t('Url')}</th>
            <th>{t('Date created')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {this.renderChampionships()}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Card id={adminSections[pageId].list}>
        <Loader
          loading={loading === true}
          overlay
        >
          {this.renderTable()}
        </Loader>
      </Card>
    );
  }

  render() {
    return (
      <Page id={pageId}>
        <>
          {this.renderHeader()}
          <ChampionshipAdminRequestsSection id={adminSections[pageId].requests} />
          <ChampionshipAdminProposesSection id={adminSections[pageId].proposes} />
          {this.renderContent()}
        </>
      </Page>
    );
  }
}

export default ChampionshipsPageView;
